import { type Metric, onCLS, onFCP, onLCP, onTTFB } from 'web-vitals';
import { type INPMetricWithAttribution, onINP } from 'web-vitals/attribution';

import type { Confidence, Trackable } from '@spotify-confidence/sdk';

import { parameterizeRoute } from './routeParameterizer';

const currentRoute = (): string => parameterizeRoute(window.location.pathname);

const coreWebVitalsTracker = (): Trackable.Manager => controller => {
  const handle = (metric: Metric) => {
    controller.track(`web-vitals-${metric.name.toLowerCase()}`, {
      value: metric.value,
      rating: metric.rating,
      route: currentRoute(),
    });
  };
  // CLS and LCP usually report at page hide; report updates immediately so the
  // SDK's regular fetch transport does not race the document unloading.
  onCLS(handle, { reportAllChanges: true });
  onFCP(handle);
  onLCP(handle, { reportAllChanges: true });
  onTTFB(handle);
};

const inpTracker = (): Trackable.Manager => controller => {
  onINP(
    (metric: INPMetricWithAttribution) => {
      const { attribution } = metric;
      controller.track('web-vitals-inp', {
        value: metric.value,
        rating: metric.rating,
        route: currentRoute(),
        interaction_target: attribution.interactionTarget,
        input_delay: attribution.inputDelay,
        processing_duration: attribution.processingDuration,
        presentation_delay: attribution.presentationDelay,
      });
    },
    { reportAllChanges: true },
  );
};

interface LoafScriptTiming extends PerformanceEntry {
  invoker: string;
  sourceURL: string;
  sourceFunctionName: string;
}

interface LoafEntry extends PerformanceEntry {
  blockingDuration: number;
  scripts: LoafScriptTiming[];
}

const LOAF_BLOCKING_DURATION_THRESHOLD_MS = 50;

const longTaskTracker = (): Trackable.Manager => controller => {
  if (typeof PerformanceObserver === 'undefined') return undefined;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        controller.track('long-task', {
          value: entry.duration,
          route: currentRoute(),
        });
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
    return () => observer.disconnect();
  } catch {
    return undefined;
  }
};

const loafTracker = (): Trackable.Manager => controller => {
  if (typeof PerformanceObserver === 'undefined') return undefined;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as LoafEntry[]) {
        if (entry.blockingDuration < LOAF_BLOCKING_DURATION_THRESHOLD_MS)
          continue;
        const worstScript = entry.scripts.reduce<LoafScriptTiming | undefined>(
          (worst, script) =>
            !worst || script.duration > worst.duration ? script : worst,
          undefined,
        );
        controller.track('long-animation-frame', {
          duration: entry.duration,
          blocking_duration: entry.blockingDuration,
          route: currentRoute(),
          script_source: worstScript?.sourceURL,
          script_function: worstScript?.sourceFunctionName,
          script_duration: worstScript?.duration,
          script_invoker: worstScript?.invoker,
        });
      }
    });
    observer.observe({ type: 'long-animation-frame', buffered: true });
    return () => observer.disconnect();
  } catch {
    return undefined;
  }
};

export const setupConfidenceTrackers = (api: Confidence): void => {
  api.track(coreWebVitalsTracker());
  api.track(inpTracker());
  api.track(longTaskTracker());
  api.track(loafTracker());
};
